import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export const Header = ({ onBookingClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/servicios', label: 'Servicios' },
    { path: '/agenda', label: 'Agenda' },
    { path: '/galeria', label: 'Galería' },
    { path: '/promos', label: 'Promos' }
  ];

  const isActive = (path) => location.pathname === path;

  const whatsappLink = "https://wa.me/528112345678?text=Hola,%20me%20gustaría%20agendar%20una%20cita";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 header-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-graphite rounded-full flex items-center justify-center">
              <span className="text-cream font-heading text-lg">S</span>
            </div>
            <span className="font-heading text-xl md:text-2xl text-graphite tracking-tight">
              Studio
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-body text-sm tracking-wide transition-colors duration-200 tab-underline ${
                  isActive(link.path)
                    ? 'text-graphite active'
                    : 'text-graphite/60 hover:text-graphite'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-btn-desktop"
              className="whatsapp-btn px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              WhatsApp
            </a>
            <Button
              onClick={onBookingClick}
              data-testid="booking-btn-desktop"
              className="bg-graphite hover:bg-graphite/90 text-cream px-6 py-2 rounded-full font-medium flex items-center gap-2 btn-scale"
            >
              <Calendar className="w-4 h-4" />
              Agendar
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-cream">
              <div className="flex flex-col h-full pt-8">
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className={`font-body text-lg py-2 border-b border-border/50 ${
                        isActive(link.path)
                          ? 'text-graphite font-semibold'
                          : 'text-graphite/60'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                <div className="mt-auto pb-8 space-y-3">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-btn-mobile-menu"
                    className="whatsapp-btn w-full py-3 rounded-xl text-center font-medium flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    WhatsApp
                  </a>
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      onBookingClick?.();
                    }}
                    data-testid="booking-btn-mobile-menu"
                    className="w-full bg-graphite hover:bg-graphite/90 text-cream py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Agendar Cita
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
